import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Archive, ArchiveRestore, Trash2, FileText, Edit3, AlertTriangle, ChevronDown, Upload, FilePlus2 } from "lucide-react";

function fmt(s) {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return s;
    }
}

export default function TemplatesListPage({ templateType = "rental_agreement" }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [category, setCategory] = useState("");
    const [sourceType, setSourceType] = useState("");
    const [archived, setArchived] = useState(false);
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [busy, setBusy] = useState(false);

    // Phase H.4 / Phase N follow-up — route-aware page chrome. The same
    // component renders the contract / ACH / invoice list pages and must
    // pivot title + breadcrumb + intro + new-route + filter labels off
    // the templateType prop only (no inference from URL or category).
    const isAch = templateType === "ach_authorization";
    const isInvoice = templateType === "invoice";
    const pageTitle = isInvoice ? "Invoice templates" : isAch ? "ACH templates" : "Contract templates";
    const breadcrumb = `Operations · ${pageTitle}`;
    const newRoutePrefix = isInvoice
        ? "/admin/templates/invoices/new"
        : isAch
            ? "/admin/templates/ach/new"
            : "/admin/templates/contracts/new";
    const introCopy = isInvoice
        ? "Build invoice templates with the rich-text editor, or upload existing PDF/DOCX files. Use {{merge tags}} to inject invoice data when generating PDFs."
        : isAch
            ? "ACH authorization templates are bundled with rental agreements when you collect bank info from a customer. Build them in the editor or upload a signed PDF/DOCX."
            : "Build templates with the rich-text editor, or upload existing PDF/DOCX files. Use {{merge tags}} to inject application data when generating contracts.";
    // Category filter on typed pages is redundant (template_type is already
    // narrowing the result set + category is mostly null for typed rows).
    // Hide it whenever templateType is set to anything we render explicitly.
    const showCategoryFilter = !isAch && !isInvoice;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/templates", {
                params: { q, category, source_type: sourceType, archived, template_type: templateType },
            });
            setItems(data.items || []);
            setCategories(data.categories || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, category, sourceType, archived, templateType]);

    useEffect(() => {
        load();
    }, [load]);

    const onSearchSubmit = (e) => {
        e.preventDefault();
        setQ(searchInput.trim());
    };

    const confirmArchive = async () => {
        if (!archiveTarget) return;
        setBusy(true);
        try {
            // Task 10 — soft archive (reversible).
            const confirmFlag = !!archiveTarget._confirmed;
            const { data } = await api.post(
                `/admin/templates/${archiveTarget.id}/archive`,
                { confirm: confirmFlag },
            );
            const refCount = Object.values(data?.references || {}).reduce((a, b) => a + b, 0);
            toast.success(refCount > 0
                ? `Template archived (was referenced by ${refCount} record${refCount === 1 ? "" : "s"})`
                : "Template archived");
            setArchiveTarget(null);
            load();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409 && detail && detail.references) {
                setArchiveTarget({
                    ...archiveTarget,
                    _refs: detail.references,
                    _confirmed: true,
                });
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    const restore = async (t) => {
        try {
            await api.post(`/admin/templates/${t.id}/unarchive`);
            toast.success("Template restored");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    // Task 10c — hard-delete pathway. Distinct from archive (which is
    // reversible). Snapshot-cascade is server-side; the dialog state
    // is held in ``deleteTarget`` (parallel to ``archiveTarget``).
    const [deleteTarget, setDeleteTarget] = useState(null);
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            const confirmFlag = !!deleteTarget._confirmed;
            const { data } = await api.post(
                `/admin/templates/${deleteTarget.id}/delete`,
                { confirm: confirmFlag },
            );
            const snapped = data?.snapshotted_into || 0;
            toast.success(snapped > 0
                ? `Template deleted. ${snapped} record${snapped === 1 ? "" : "s"} snapshotted.`
                : "Template deleted.");
            setDeleteTarget(null);
            load();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409 && detail && detail.references) {
                setDeleteTarget({
                    ...deleteTarget,
                    _refs: detail.references,
                    _confirmed: true,
                });
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <DashboardShell>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                        {breadcrumb}
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        {pageTitle}
                    </h1>
                    <p className="mt-2 max-w-xl text-sm text-slate-600 leading-relaxed">
                        {introCopy}
                    </p>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button data-testid="new-template-button" className="bg-blue-900 hover:bg-blue-800 gap-2">
                            <Plus size={16} /> New template <ChevronDown size={14} />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`${newRoutePrefix}?mode=builder`)} data-testid="new-template-builder">
                            <FilePlus2 size={14} className="mr-2" /> Build in editor
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`${newRoutePrefix}?mode=upload`)} data-testid="new-template-upload">
                            <Upload size={14} className="mr-2" /> Upload PDF or DOCX
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <div className="mt-6 flex flex-col md:flex-row md:items-center gap-3">
                <form onSubmit={onSearchSubmit} className="flex gap-2 items-center" data-testid="templates-search">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="templates-search-input"
                            placeholder="Search templates…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="pl-8 w-64"
                        />
                    </div>
                    <Button type="submit" variant="outline" data-testid="templates-search-submit">Search</Button>
                </form>
                {showCategoryFilter && (
                    <select
                        data-testid="templates-category-filter"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="h-10 rounded-md border border-slate-200 bg-white text-sm px-3"
                    >
                        <option value="">All categories</option>
                        {categories.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                )}
                <select
                    data-testid="templates-source-filter"
                    value={sourceType}
                    onChange={(e) => setSourceType(e.target.value)}
                    className="h-10 rounded-md border border-slate-200 bg-white text-sm px-3"
                >
                    <option value="">All sources</option>
                    <option value="builder">Builder</option>
                    <option value="upload">Upload</option>
                </select>
                <label className="ml-auto inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                        type="checkbox"
                        data-testid="templates-show-archived"
                        checked={archived}
                        onChange={(e) => setArchived(e.target.checked)}
                        className="rounded border-slate-300"
                    />
                    Show archived
                </label>
            </div>

            <Card className="mt-5">
                <CardContent className="p-0">
                    <Table data-testid="templates-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead>Tags</TableHead>
                                <TableHead>Updated</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-sm text-slate-500 py-10">Loading…</TableCell>
                                </TableRow>
                            )}
                            {!loading && items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center text-sm text-slate-500 py-12">
                                        <FileText className="mx-auto mb-3 text-slate-300" size={28} />
                                        No templates {archived ? "archived" : "yet"}.
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && items.map((t) => (
                                <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                                    <TableCell className="font-medium text-slate-900">
                                        <Link to={`/admin/templates/${t.id}`} className="hover:underline" data-testid={`template-name-${t.id}`}>
                                            {t.name}
                                        </Link>
                                        {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description.slice(0, 80)}</div>}
                                    </TableCell>
                                    <TableCell>
                                        {t.category ? (
                                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 capitalize">{t.category}</span>
                                        ) : (
                                            <span className="text-slate-400 text-sm">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${t.source_type === "builder" ? "bg-blue-50 text-blue-900" : "bg-amber-50 text-amber-900"}`}>
                                            {t.source_type}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="inline-flex items-center gap-2 text-xs">
                                            <span className="rounded-full bg-emerald-50 text-emerald-900 px-2 py-0.5 font-medium">
                                                {t.detected_tags.length} detected
                                            </span>
                                            {t.unknown_tags.length > 0 && (
                                                <span data-testid={`template-unknown-${t.id}`} className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 font-medium">
                                                    <AlertTriangle size={10} /> {t.unknown_tags.length} unknown
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-600">{fmt(t.updated_at)}</TableCell>
                                    <TableCell className="text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <Button asChild size="sm" variant="ghost" data-testid={`template-edit-${t.id}`} className="gap-1.5">
                                                <Link to={`/admin/templates/${t.id}`}>
                                                    <Edit3 size={14} /> {t.source_type === "builder" ? "Edit" : "View"}
                                                </Link>
                                            </Button>
                                            {!t.archived ? (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => setArchiveTarget(t)}
                                                    data-testid={`template-archive-${t.id}`}
                                                    className="gap-1.5 text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                                                >
                                                    <Archive size={14} /> Archive
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => restore(t)}
                                                    data-testid={`template-restore-${t.id}`}
                                                    className="gap-1.5"
                                                >
                                                    <ArchiveRestore size={14} /> Restore
                                                </Button>
                                            )}
                                            {/* Task 10c — additional hard-delete action.
                                                Visible on every row (archived or not) so an
                                                admin can purge a row directly. */}
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setDeleteTarget(t)}
                                                data-testid={`template-delete-${t.id}`}
                                                className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2 size={14} /> Delete
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
                <AlertDialogContent data-testid="archive-template-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {archiveTarget?._refs ? "Archive anyway?" : "Archive this template?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm text-slate-600">
                                <p>
                                    &ldquo;{archiveTarget?.name}&rdquo; will be hidden from the default list and from
                                    pickers. You can restore it later from the archived view.
                                </p>
                                {archiveTarget?._refs && (
                                    <div
                                        className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900"
                                        data-testid="archive-template-refs"
                                    >
                                        <p className="font-medium">This template is referenced by:</p>
                                        <ul className="list-disc list-inside mt-1 text-xs">
                                            {Object.entries(archiveTarget._refs).map(([k, v]) => (
                                                <li key={k}>
                                                    <span className="font-semibold">{v}</span>{" "}
                                                    {k.replace(/_/g, " ")}
                                                </li>
                                            ))}
                                        </ul>
                                        <p className="mt-2 text-xs">
                                            Existing records will continue to display correctly, but no
                                            new draft can pick this template.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="archive-template-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmArchive} disabled={busy} data-testid="archive-template-confirm" className="bg-amber-600 hover:bg-amber-700">
                            {busy ? "Archiving…" : (archiveTarget?._refs ? "Archive anyway" : "Archive")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Task 10c — hard-delete confirm dialog. Visually
                distinct from the archive dialog above: red destructive
                button + explicit "permanent / cannot be undone" copy. */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-template-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {deleteTarget?._refs ? "Delete permanently anyway?" : "Delete this template permanently?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm text-slate-600">
                                <p>
                                    &ldquo;{deleteTarget?.name}&rdquo; will be{" "}
                                    <span className="font-semibold text-red-700">permanently removed</span>.
                                    This action cannot be undone.
                                </p>
                                {deleteTarget?._refs ? (
                                    <div
                                        className="rounded-md border border-red-200 bg-red-50 p-3 text-red-900"
                                        data-testid="delete-template-refs"
                                    >
                                        <p className="font-medium">This template is referenced by:</p>
                                        <ul className="list-disc list-inside mt-1 text-xs">
                                            {Object.entries(deleteTarget._refs).map(([k, v]) => (
                                                <li key={k}>
                                                    <span className="font-semibold">{v}</span>{" "}
                                                    {k.replace(/_/g, " ")}
                                                </li>
                                            ))}
                                        </ul>
                                        <p className="mt-2 text-xs">
                                            The template content will be <span className="font-semibold">snapshotted</span>{" "}
                                            into each referencing record before deletion, so historical
                                            documents keep rendering correctly. New records won&apos;t be
                                            able to select this template.
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500">
                                        It is not referenced anywhere &mdash; safe to remove.
                                    </p>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-template-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} disabled={busy} data-testid="delete-template-confirm" className="bg-red-600 hover:bg-red-700">
                            {busy ? "Deleting…" : (deleteTarget?._refs ? "Delete anyway" : "Delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardShell>
    );
}
